import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import StudentGame from '@/game/GameComponents/PhaserGame/StudentGames/StudentGame';
import { MINIGAME_CARD_SOUND_OPTIONS } from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  isLetter,
  SizedObject,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import PlayerTypingInput from '@/game/prefabs/StudentGame/PlayerTypingInput';

export class StudentGameKeyboardInputHandler {
  constructor(private studentGame: StudentGame) {
    this.studentGame = studentGame;

    // Listen for keyboard input.
    studentGame
      .getScene()
      .input.keyboard?.on('keydown', this.handleKeyboardInput, this);
  }

  maxAttemptStringLength: number = 60;

  getCurrentAttemptString(): string {
    return this.studentGame.getTypedInput();
  }

  handleKeyboardInput(event: any, forceInput?: boolean) {
    // First validate that we're actually allowed to input text.
    if (!forceInput && this.studentGame.boardInteractive === false) return;
    if (!forceInput && this.studentGame.inputHandler?.currInputSystem !== this)
      return;

    this.clearAnimations();
    if (this.isValidInput(event.key)) {
      this.addCharacterToAttemptString(event.key);
    } else if (event.key === 'Backspace') {
      this.handleBackspace();
    } else if (event.key === 'Enter') {
      this.handleSubmission();
      return;
    } else if (event.key === 'ArrowLeft') {
      this.handleLeftArrow();
    } else if (event.key === 'ArrowRight') {
      this.handleRightArrow();
    }

    this.updateFontSize();

    EventBus.emit(
      GAME_EVENTS.STUDENT_GAME_KEYBOARD_INPUT,
      this.getCurrentAttemptString(),
    );
    if (!forceInput) {
      this.studentGame.setSubmissionPossible(this.studentGame.getCanSubmit());
    }
  }

  addCharacterToAttemptString(character: string) {
    const currentAttemptString = this.getCurrentAttemptString();
    // Impose maximum character limit.
    if (currentAttemptString.length >= this.maxAttemptStringLength) return;

    // No double spaces or leading spaces.
    if (
      character === ' ' &&
      (currentAttemptString.endsWith(' ') || currentAttemptString.length === 0)
    )
      return;

    const scene = this.studentGame.getScene();
    this.studentGame
      .getPlayerTypingInputs()
      .forEach((input: PlayerTypingInput) => {
        input.addCharacter(character);
      });
    PhaserAudioManager.playSFX(
      scene,
      'card-select',
      MINIGAME_CARD_SOUND_OPTIONS,
    );
  }

  handleBackspace() {
    const scene = this.studentGame.getScene();
    this.studentGame
      .getPlayerTypingInputs()
      .forEach((input: PlayerTypingInput) => {
        input.removeCharacter();
      });
    PhaserAudioManager.playSFX(
      scene,
      'card-select',
      MINIGAME_CARD_SOUND_OPTIONS,
    );
    this.updateFontSize();
  }

  handleSubmission() {
    if (this.studentGame.getCanSubmit() === false) return;
    // Play the submit SFX.
    PhaserAudioManager.playSFX(
      this.studentGame.getScene(),
      'submit-click',
      MINIGAME_CARD_SOUND_OPTIONS,
    );
    this.studentGame.checkAnswer();
  }

  handleLeftArrow() {
    this.studentGame
      .getPlayerTypingInputs()
      .forEach((input: PlayerTypingInput) => input.moveCursorLeft());
  }

  handleRightArrow() {
    this.studentGame
      .getPlayerTypingInputs()
      .forEach((input: PlayerTypingInput) => input.moveCursorRight());
  }

  clearAnimations() {
    this.studentGame
      .getPlayerTypingInputs()
      .forEach((input: PlayerTypingInput) => {
        input.getCharacterArray().forEach((element) => {
          element.killInitialAnimation();
        });
      });
  }

  isValidInput(s: string): boolean {
    const validInputs = [' ', '.'];
    return (isLetter(s) as boolean) || validInputs.includes(s);
  }

  async updateFontSize() {
    // Update font size.
    const fontSizePref = -64;
    const fontSizeMin = -18;

    const typingInputs = this.studentGame.getPlayerTypingInputs();

    typingInputs.forEach(async (playerTypingInput: PlayerTypingInput) => {
      const letters = playerTypingInput
        .getTypedTextLayoutGroup()
        .getElementList();
      const padding = 40;
      const maxWidth = playerTypingInput.typingBacking.displayWidth - padding;
      let fontSize = fontSizePref;
      let totalWidth = 0;

      const updateTotalWidth = () => {
        totalWidth = 0;
        letters.forEach((letter) => {
          totalWidth += (letter as SizedObject).getBounds().width + 4;
        });
      };

      // Initialize the font size to preferred.
      const charArray = playerTypingInput.getCharacterArray();
      for (var i = 0; i < charArray.length; i++) {
        charArray[i].setFontSize(fontSize);
      }
      updateTotalWidth();

      while (totalWidth > maxWidth) {
        fontSize += 1;
        const charArray = playerTypingInput.getCharacterArray();
        for (var i = 0; i < charArray.length; i++) {
          charArray[i].setFontSize(fontSize);
        }
        updateTotalWidth();
        //await waitForMilliseconds(playerTypingInput.scene.time, 1);
      }

      playerTypingInput.getTypedTextLayoutGroup().updateLayout();
    });
  }
}
